import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../state/store";
import { EChartsOption } from "echarts";
import BaseChart from "../../../baseChart";
import { numFormatter } from "../../../../helpers/utils";
import { formatDateTime } from "../../barComboWidget/utils";

export type LineChartConfiguration = {
  currency_formatter?: string;
  visual: {
    value_config: {
      name: string;
      accessor: string;
      aggregation_type: string;
      is_monetary_value_type: boolean;
    };
    category_config: {
      accessor: string;
      name: string;
    };
    series_config: {
      accessor: string;
      name: string;
    };
    group_by_accessor: string;
    show_data_zoom: boolean;
    smooth_line: boolean;
    show_legend: boolean;
  };
  filter: {
    type: string;
    id_accessor: string;
    name_accessor: string;
  };
  value_formatter: { prefix: string; postfix: string };
};

export type LineChartData = {
  [key: string]: any;
}[];

interface ILineChartProps {
  width: number;
  height: number;
  data: LineChartData;
  config: LineChartConfiguration;
  extraConfigs?: any;
}

const LineChart = ({
  data,
  config,
  width,
  height,
  extraConfigs,
}: ILineChartProps) => {
  const theme = useSelector((state: RootState) => state.theme);

  const { seriesData, categories } = useMemo(() => {
    if (!data || data.length === 0 || !config?.visual) {
      return { seriesData: [], categories: [] };
    }

    const { visual } = config;
    const categoryAccessor = visual.category_config.accessor;
    const valueAccessor = visual.value_config.accessor;
    const seriesAccessor = visual.series_config.accessor;
    const groupByAccessor = visual.group_by_accessor;

    // Extract all unique categories (x-axis) from the result set in the order they appear in data
    const allCategories = data.map((r) => r[categoryAccessor]?.toString() || '').filter(category => category).reduce((unique: string[], category: string) => {
      if (!unique.includes(category)) {
        unique.push(category);
      }
      return unique;
    }, []);

    // Extract all unique series (staff names) from the result set
    const allSeries = data.map((r) => r[seriesAccessor]?.toString() || '').filter(series => series).reduce((unique: string[], series: string) => {
      if (!unique.includes(series)) {
        unique.push(series);
      }
      return unique;
    }, []);
    
    // Group data by series and category
    const seriesMap: { [seriesName: string]: { [category: string]: number } } = {};
    
    data.forEach((r) => {
      const category = r[groupByAccessor]?.toString() || '';
      const seriesName = r[seriesAccessor]?.toString() || '';
      const value = r[valueAccessor] || 0;
      
      if (category && seriesName) {
        if (!seriesMap[seriesName]) {
          seriesMap[seriesName] = {};
        }
        // Aggregate values if multiple entries for same series-category combination
        seriesMap[seriesName][category] = (seriesMap[seriesName][category] || 0) + value;
      }
    });

    // Create series data for each staff member
    const chartSeriesData = allSeries.map((seriesName: string) => ({
      name: seriesName,
      data: allCategories.map((category: string) => seriesMap[seriesName]?.[category] || 0)
    }));
    
    return { seriesData: chartSeriesData, categories: allCategories };
  }, [data, config]);

  const option = useMemo<EChartsOption>(() => {
    let updatedOption: EChartsOption = {};
    if (data && seriesData.length > 0 && config?.visual) {
      const { visual } = config;
      const valueFormatter = config.value_formatter?.postfix || config.currency_formatter || '';
      
      // Show legend if configured and there are multiple series
      if (visual.show_legend && seriesData.length > 1) {
        updatedOption.legend = {
          show: true,
          top: "center",
          right: "10px",
          type: "scroll",
          orient: "vertical",
          data: seriesData.map((s: any) => s.name),
        };
        if (width < 650) {
          updatedOption.legend = {
            ...updatedOption.legend,
            top: "10px",
            right: 0,
            type: "scroll",
            orient: "horizontal",
          };
        } else {
          updatedOption.grid = {
            right: "160px",
          };
        }
      }
      
      updatedOption.xAxis = {
        type: "category",
        data: categories,
        axisPointer: {
          show: true,
          type: "shadow",
        },
      };
      if (extraConfigs && data[0]?.offset) {
        const offset = data[0]?.offset;
        const isTrendsHourly =
          extraConfigs.trendPeriod.includes("trends-hourly");
        if (isTrendsHourly) {
          updatedOption.xAxis = {
            type: "category",
            data: categories.map((c: any) =>
              formatDateTime(c, offset, "dd/MM - hh:mm a")
            ),
            axisPointer: {
              show: true,
              type: "shadow",
            },
          };
        }
      }
      
      updatedOption.yAxis = {
        type: "value",
        axisLabel: {
          formatter: (value) => `${numFormatter(value)} ${valueFormatter}`,
        },
        axisPointer: {
          show: true,
          type: "line",
        },
      };
      
      // Show data zoom only if configured
      if (visual.show_data_zoom) {
        updatedOption.dataZoom = {
          type: "slider",
          height: 10,
          startValue: 0,
          endValue: Math.min(9, categories.length - 1),
          backgroundColor: "#F1EDED",
          fillerColor: "#717171",
          brushSelect: false,
          moveHandleSize: 0,
          moveHandleIcon: "diamond",
          handleIcon: "circle",
          handleSize: 12,
        };
      }
      
      updatedOption.tooltip = {
        trigger: "axis",
        valueFormatter: (value) => `${value} ${valueFormatter}`,
        axisPointer: {
          show: true,
          type: "cross",
        },
      };
      
      updatedOption.series = seriesData.map((series: any) => ({
        name: series.name,
        type: "line",
        data: series.data,
        smooth: visual.smooth_line,
        symbol: "circle",
        symbolSize: 6,
        lineStyle: {
          width: 3,
        },
      }));
      
      updatedOption.grid = {
        left: "3%",
        right: visual.show_legend && seriesData.length > 1 && width >= 650 ? "160px" : "4%",
        bottom: visual.show_data_zoom ? "15%" : "10%",
        containLabel: true,
      };
    }
    return updatedOption;
  }, [data, config, seriesData, categories, width]);

  const handleClickEvent = (e: any) => {
    // TODO: Handle click events if needed
  };

  return (
    <BaseChart
      height={height + 20}
      width={width}
      options={option}
      echartsTheme={theme.mode}
      chartType="line"
      onClick={handleClickEvent}
    />
  );
};

export default LineChart;
